/* ═══════════════════════════════════════════════════════════════════════
   assets/quiz-dfco-walk.js

   DFCo "Walk Diagnostic" quiz runtime. Vanilla JS, no build step, IIFE-scoped.
   Forked from the PetGently quiz runtime. Key differences:
     • Scoped .quiz-dfw / qdfw- ; storage key quiz:dfco-walk.
     • Routing is multi_category BUCKET SCORING (not PetGently's hard-override):
         1. sum ft/gw/ab/te across all answers
         2. apply the Q6 multi-select bonus (3+ → te +2), read from the block
         3. highest bucket wins
         4. tie → the Q4 (tiebreak-anchor) bucket wins if it's tied
         5. else fixed priority gw > ft > ab > te
         6. fallback bucket (data-fallback-bucket, default ft) — unreachable in
            practice because Q4 + Q5 each guarantee +3 to one bucket.
     • Interstitials advance on a normal Continue (no read-pause).
     • Per-block affirmation / loading durations are actually honoured (read from
       the inner element, fixing a latent PetGently bug).
     • Refresh mid-quiz restores answer visual state (checked inputs + selected
       cards + text value), per the brief's re-entry requirement.
     • Slim Aeyla-style tracker fires the brief's §7 events to webhook/Pixel/GA4.

   v2 (client feedback round 1):
     • ANSWER SELECTION REWIRED — pointer taps are handled on the answer row
       (label) itself with manual input-state management, instead of relying on
       the input's `change` event. `change` never re-fires on an already-checked
       radio, which is exactly the state after using Back — that was the
       "I can't click the same answer to continue" bug, and the main source of
       taps that seemed to do nothing. Keyboard / assistive tech still works via
       a `change` listener on the input (no double-handling: label clicks are
       preventDefault'ed).
     • SINGLE-FIRE ADVANCE GUARDS — every advance trigger (auto-advance timer,
       Continue buttons, Enter on the name field) is debounced and guarded, so
       rapid double-taps can no longer fire advanceFrom() twice (which skipped
       ahead and polluted Back history). Re-tapping a different answer inside
       the auto-advance window simply restarts the timer; the last selection wins.
     • PROGRESS BAR — see v2.1 below (model replaced on client feedback).
     • Back navigation re-dispatches quiz:block-shown so a revisited block
       re-arms its guards.
     • New `social-proof` block type binds like an interstitial (view event +
       Continue).

   v2.1 (client feedback round 2):
     • PROGRESS = CLASSIC PER-SLIDE FILL — every slide after the landing is one
       step (questions, affirmations, interstitials, social proof, loading).
       Each advance fills the bar by one increment; 100% on the loading slide;
       Back walks it down. The header label shows the matching percent (was
       "N / 11"), so the number and the bar always move in lockstep.

   v2.3 (client feedback round 3):
     • HEADER: moved above the landing in the DOM and visibility is now
       class-gated — updateChrome toggles .quiz-dfw--chrome on the section
       root; CSS shows the header only when that class is present. Fixes the
       stray back-button + progress track rendering at the BOTTOM of the
       landing. All inline display writes removed (single source of truth).
     • ONE ACTIVE BLOCK, ALWAYS: transitionTo and goBack defensively strip
       --active from every other block (the landing's is server-rendered).
     • Sticky CTA crop fixed in CSS: block enter/exit animations are now
       opacity-only — the retained translateY(0) made each active block a
       containing block for position:fixed, anchoring the CTA below the fold.

   Personalization passed to the result page: dog_name (+ q_<id> answer params).
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-quiz-section]');
    if (!root) return;

    // In the Shopify Theme Editor, show every slide stacked for editing and skip
    // the runtime state machine entirely (no auto-advance, no redirect to result
    // pages). The CSS (.quiz-dfw--design-mode) does the visual reveal. The live
    // storefront never has design mode, so its one-at-a-time flow is unaffected.
    if (window.Shopify && Shopify.designMode) {
      root.classList.add('quiz-dfw--design-mode');
      return;
    }

    var debug = root.dataset.debug === 'true';
    var log = debug
      ? function () { console.log.apply(console, ['[DFCoWalkQuiz]'].concat([].slice.call(arguments))); }
      : function () {};

    /* ───── DOM refs ───── */
    var blocks = Array.prototype.slice.call(root.querySelectorAll('.quiz-dfw-block'));
    if (blocks.length === 0) { log('No blocks found.'); return; }

    var headerEl = root.querySelector('[data-quiz-header]');
    var backBtn = root.querySelector('[data-quiz-back]');
    var stepLabel = root.querySelector('[data-quiz-step]');
    var progressFill = root.querySelector('[data-quiz-progress]');
    var startBtn = root.querySelector('[data-quiz-start]');

    /* ───── Config (data attributes) ───── */
    var storageKey = root.dataset.storageKey || 'quiz:dfco-walk';
    var forwardUtm = root.dataset.forwardUtm === 'true';
    var fallbackUrl = root.dataset.fallbackUrl || '/';
    var fallbackBucket = root.dataset.fallbackBucket || 'ft';

    var resultUrls = {
      ft: root.dataset.urlFt || fallbackUrl,
      gw: root.dataset.urlGw || fallbackUrl,
      ab: root.dataset.urlAb || fallbackUrl,
      te: root.dataset.urlTe || fallbackUrl
    };

    /* ───── State ───── */
    var sessionId = generateSessionId();
    var startedAt = Date.now();
    var history = [];
    var currentIdx = -1; // -1 = on the landing screen
    var hasLanding = !!root.querySelector('.quiz-dfw-block--landing');
    var completed = false;
    var startFired = false;

    // answers[idx] = { type, block_id, block_idx, selections:[{value,text,bucket,weight}],
    //                  is_tiebreak_anchor, multi_bonus:{bucket,threshold,weight} }
    var answers = {};

    var personalization = { dog_name: '' };

    /* ───── Tracking (Aeyla-style, slim) ───── */
    var tracker = createTracker(root, sessionId, log);

    log('Initialized', { blockCount: blocks.length, hasLanding: hasLanding, resultUrls: resultUrls });

    rehydrate();
    bindLanding();
    bindAllBlocks();
    resumeActiveBlock();      // re-fire behaviour + restore visuals if rehydrated mid-quiz

    if (!hasLanding && currentIdx === -1) {
      goToBlock(0);
    }
    updateChrome();

    tracker.track('quiz_view', { has_landing: hasLanding });

    window.addEventListener('beforeunload', function () {
      if (!completed) {
        tracker.track('quiz_abandon', {
          last_block_id: (blocks[currentIdx] && blocks[currentIdx].dataset.blockId) || '',
          step_index: currentIdx,
          seconds: Math.round((Date.now() - startedAt) / 1000)
        });
      }
    });

    /* ════════ Landing ════════ */

    function bindLanding() {
      if (!startBtn) return;
      startBtn.addEventListener('click', function () {
        var landing = root.querySelector('.quiz-dfw-block--landing');
        if (landing) landing.classList.remove('quiz-dfw-block--active');
        var firstIdx = blocks.findIndex(function (b) { return b.dataset.blockType !== 'landing'; });
        if (firstIdx < 0) firstIdx = 0;
        fireStart();
        goToBlock(firstIdx);
      });
    }

    function fireStart() {
      if (startFired) return;
      startFired = true;
      tracker.track('quiz_start', {});
    }

    /* ════════ Block binding ════════ */

    function bindAllBlocks() {
      blocks.forEach(function (block, idx) {
        switch (block.dataset.blockType) {
          case 'quiz-question': bindQuizQuestion(block, idx); break;
          case 'text-input':    bindTextInput(block, idx); break;
          case 'interstitial':  bindContinueSlide(block, idx); break;
          case 'social-proof':  bindContinueSlide(block, idx); break;
          case 'affirmation':   bindAffirmation(block, idx); break;
          case 'loading':       bindLoading(block, idx); break;
        }
      });
      if (backBtn) backBtn.addEventListener('click', goBack);
    }

    /* ════════ Quiz question (single + multi) ════════
       Pointer interaction is handled on the .qdfw-arow label with manual
       checked-state management. Rationale: the old wiring listened only for
       `change` on the hidden input, and `change` does not fire when you click
       a radio that is ALREADY checked — which is the state after Back. That
       made the previously chosen answer a dead zone, and any tap that didn't
       flip the input's state did nothing. Click handling on the row responds
       every time, including re-taps of the same answer.                       */

    function bindQuizQuestion(block, idx) {
      var labels = Array.prototype.slice.call(block.querySelectorAll('.qdfw-arow'));
      var inputs = Array.prototype.slice.call(block.querySelectorAll('.qdfw-answer__input'));
      var inputType = inputs.length > 0 ? inputs[0].type : 'radio';
      var nextBtn = block.querySelector('.qdfw-next');

      var advanceTimer = null;   // pending auto-advance (single-select)
      var advancing = false;     // an advance has been committed for this visit

      // Re-entering the block (Back, or resume after refresh) re-arms it.
      block.addEventListener('quiz:block-shown', function () {
        advancing = false;
        if (advanceTimer) { clearTimeout(advanceTimer); advanceTimer = null; }
      });

      function pressFeedback(label) {
        label.classList.add('qdfw-press--pressed');
        setTimeout(function () { label.classList.remove('qdfw-press--pressed'); }, 240);
      }

      function syncNextState() {
        if (!nextBtn) return;
        var minSel = parseInt(nextBtn.dataset.minSelections, 10);
        if (isNaN(minSel)) minSel = 1;
        var checked = block.querySelectorAll('.qdfw-answer__input:checked').length;
        nextBtn.disabled = checked < minSel;
      }

      function selectRadio(label, input) {
        if (advancing) return;
        labels.forEach(function (l) { l.classList.remove('qdfw-arow--selected'); });
        inputs.forEach(function (i) { i.checked = (i === input); });
        label.classList.add('qdfw-arow--selected');
        pressFeedback(label);

        // Forced-Continue single-select (show_continue): no auto-advance —
        // selecting just enables the button.
        if (nextBtn) { syncNextState(); return; }

        // Debounced auto-advance: a re-tap (same or different answer) restarts
        // the timer, the last selection wins, and only ONE advance ever fires.
        if (advanceTimer) clearTimeout(advanceTimer);
        advanceTimer = setTimeout(function () {
          advanceTimer = null;
          if (advancing || currentIdx !== idx) return; // user already left (e.g. tapped Back)
          advancing = true;
          finalizeQuestionAnswer(block, idx);
          advanceFrom(idx);
        }, autoAdvanceDelay());
      }

      function toggleCheckbox(label, input, forceState) {
        if (advancing) return;
        input.checked = (typeof forceState === 'boolean') ? forceState : !input.checked;
        label.classList.toggle('qdfw-arow--selected', input.checked);
        pressFeedback(label);
        syncNextState();
      }

      labels.forEach(function (label) {
        var input = label.querySelector('.qdfw-answer__input');
        if (!input) return;

        // Pointer / touch path — deterministic, fires on every tap.
        label.addEventListener('click', function (e) {
          e.preventDefault(); // we manage the input state ourselves
          if (inputType === 'radio') selectRadio(label, input);
          else toggleCheckbox(label, input);
        });

        // Keyboard / assistive-tech path — native input interaction.
        // (Label clicks never reach here because of preventDefault above.)
        input.addEventListener('change', function () {
          if (inputType === 'radio') selectRadio(label, input);
          else toggleCheckbox(label, input, input.checked);
        });
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (nextBtn.disabled || advancing) return;
          advancing = true;
          finalizeQuestionAnswer(block, idx);
          advanceFrom(idx);
        });
      }
    }

    function finalizeQuestionAnswer(block, idx) {
      var checkedInputs = Array.prototype.slice.call(block.querySelectorAll('.qdfw-answer__input:checked'));
      var selections = checkedInputs.map(function (inp) {
        var label = inp.closest('.qdfw-arow');
        return {
          value: label.dataset.answerValue,
          text: label.dataset.answerText,
          bucket: label.dataset.answerBucket || 'none',
          weight: parseInt(label.dataset.answerWeight, 10) || 0
        };
      });

      answers[idx] = {
        type: 'quiz-question',
        block_id: block.dataset.blockId,
        block_idx: idx,
        selections: selections,
        is_tiebreak_anchor: block.dataset.tiebreakAnchor === 'true',
        multi_bonus: {
          bucket: block.dataset.multiBonusBucket || 'none',
          threshold: parseInt(block.dataset.multiBonusThreshold, 10) || 0,
          weight: parseInt(block.dataset.multiBonusWeight, 10) || 0
        }
      };

      // Generic personalization capture (DFCo only uses dog_name, but future-proof)
      var pkey = block.dataset.personalizationKey;
      if (pkey && selections.length > 0) {
        personalization[pkey] = selections[0].value;
        personalization[pkey + '_label'] = selections[0].text;
      }

      persist();
      tracker.track('question_complete', {
        question_id: block.dataset.blockId,
        answer_values: selections.map(function (s) { return s.value; })
      });
      log('Question answered', { idx: idx, selections: selections });
    }

    /* ════════ Text input (Q1 dog name) ════════ */

    function bindTextInput(block, idx) {
      var input = block.querySelector('[data-text-input]');
      var nextBtn = block.querySelector('.qdfw-next');
      if (!input) return;

      var advancing = false;
      block.addEventListener('quiz:block-shown', function () { advancing = false; });

      // Q1 is optional (brief §3): Continue stays enabled and an empty name is
      // allowed — it falls back to "your dog" everywhere downstream.
      function submit() {
        if (advancing) return;
        advancing = true;
        finalizeTextInput(block, idx, input);
        advanceFrom(idx);
      }

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          submit();
        }
      });
      if (nextBtn) nextBtn.addEventListener('click', submit);
    }

    function finalizeTextInput(block, idx, input) {
      var pkey = input.dataset.personalizationTarget || 'dog_name';
      var val = input.value.trim();
      if (val) val = val.charAt(0).toUpperCase() + val.slice(1);
      answers[idx] = { type: 'text-input', block_id: block.dataset.blockId, block_idx: idx, value: val };
      personalization[pkey] = val;
      persist();
      tracker.track('question_complete', { question_id: block.dataset.blockId, answer_values: val ? ['(named)'] : [] });
      log('Text input', { key: pkey, value: val });
    }

    /* ════════ Continue slides: interstitial + social proof ════════ */

    function bindContinueSlide(block, idx) {
      var nextBtn = block.querySelector('.qdfw-next');
      var advancing = false;

      block.addEventListener('quiz:block-shown', function () {
        advancing = false;
      });
      block.addEventListener('quiz:block-shown', function () {
        tracker.track('interstitial_view', { interstitial_id: block.dataset.blockId });
      }, { once: true });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (advancing) return;
          advancing = true;
          answers[idx] = { type: block.dataset.blockType, block_id: block.dataset.blockId, viewed: true };
          persist();
          advanceFrom(idx);
        });
      }
    }

    /* ════════ Affirmation (auto-advancing) ════════ */

    function bindAffirmation(block, idx) {
      block.addEventListener('quiz:block-shown', function () {
        var inner = block.querySelector('.qdfw-affirmation');
        var duration = parseInt(inner && inner.dataset.autoAdvanceMs, 10) || 1200;
        setTimeout(function () { advanceFrom(idx); }, duration);
      }, { once: true });
    }

    /* ════════ Loading (3-state crossfade → route) ════════ */

    function bindLoading(block, idx) {
      block.addEventListener('quiz:block-shown', function () {
        var el = block.querySelector('.qdfw-loading');
        var total = parseInt(el && el.dataset.totalMs, 10) || 4000;
        var states = Array.prototype.slice.call(block.querySelectorAll('.qdfw-loading__state'));
        var count = states.length || 1;
        var stateDuration = total / count;
        var current = 0;

        var interval = setInterval(function () {
          current++;
          if (current >= count) { clearInterval(interval); return; }
          states.forEach(function (s, i) { s.classList.toggle('qdfw-loading__state--active', i === current); });
        }, stateDuration);

        setTimeout(function () { completeAndRoute(); }, total);
      }, { once: true });
    }

    /* ════════ Navigation ════════ */

    function advanceFrom(idx) {
      fireStart();
      var nextIdx = idx + 1;
      if (nextIdx >= blocks.length) { completeAndRoute(); return; }
      goToBlock(nextIdx);
    }

    function goToBlock(idx) {
      if (idx < 0 || idx >= blocks.length) return;
      var prevBlock = blocks[currentIdx];
      var nextBlock = blocks[idx];

      if (currentIdx >= 0 && history[history.length - 1] !== currentIdx) {
        history.push(currentIdx);
      }

      if (prevBlock) {
        prevBlock.classList.remove('quiz-dfw-block--active');
        prevBlock.classList.add('quiz-dfw-block--exiting');
        setTimeout(function () { prevBlock.classList.remove('quiz-dfw-block--exiting'); }, 400);
      }

      // Defensive (v2.3): exactly ONE block may be active — strips any stray
      // --active (e.g. the landing's server-rendered one) before activating.
      blocks.forEach(function (b) {
        if (b !== nextBlock) b.classList.remove('quiz-dfw-block--active');
      });

      currentIdx = idx;
      nextBlock.classList.add('quiz-dfw-block--active');
      applyPersonalization(nextBlock);
      nextBlock.dispatchEvent(new CustomEvent('quiz:block-shown', { detail: { idx: idx } }));

      updateChrome();
      persist();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function goBack() {
      if (history.length === 0) return;
      var prevIdx = history.pop();
      // Skip back over affirmations (non-interactive)
      while (prevIdx >= 0 && blocks[prevIdx].dataset.blockType === 'affirmation' && history.length > 0) {
        prevIdx = history.pop();
      }
      if (prevIdx < 0) return;
      var cur = blocks[currentIdx];
      var target = blocks[prevIdx];
      blocks.forEach(function (b) {
        if (b !== target) b.classList.remove('quiz-dfw-block--active');
      });
      if (cur) cur.classList.remove('quiz-dfw-block--active');
      currentIdx = prevIdx;
      target.classList.add('quiz-dfw-block--active');
      applyPersonalization(target);
      // Re-arm the revisited block (resets its advance guards; interstitial
      // view tracking is { once: true } so it can't double-fire).
      target.dispatchEvent(new CustomEvent('quiz:block-shown', { detail: { idx: prevIdx, via: 'back' } }));
      updateChrome();
      persist();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function updateChrome() {
      if (!headerEl) return;

      var cur = blocks[currentIdx];
      var type = cur && cur.dataset.blockType;
      var isLanding = currentIdx < 0 || type === 'landing';
      var isLoading = type === 'loading';
      var isAffirmation = type === 'affirmation';

      // v2.3: header visibility is class-gated on the section root — CSS shows
      // it only when .quiz-dfw--chrome is present. No inline display juggling.
      if (isLanding) { root.classList.remove('quiz-dfw--chrome'); return; }
      root.classList.add('quiz-dfw--chrome');

      if (backBtn) {
        var canBack = history.length > 0 && !isLoading && !isAffirmation;
        backBtn.style.display = canBack ? '' : 'none';
      }

      // Classic progress bar (client round 2): EVERY slide after the landing
      // is a step — questions, affirmations, interstitials, social proof, and
      // the loading screen all count. Each advance fills the bar by one
      // increment; it reaches 100% on the final (loading) slide. Going Back
      // walks it down again. Label shows the same percent so the number and
      // the bar always move together.
      var totalSteps = 0;
      var position = 0;
      blocks.forEach(function (b, i) {
        if (b.dataset.blockType === 'landing') return;
        totalSteps++;
        if (i <= currentIdx) position++;
      });

      var pct = totalSteps > 0 ? Math.round((position / totalSteps) * 100) : 0;

      if (stepLabel) {
        stepLabel.textContent = pct + '%';
      }
      if (progressFill) {
        progressFill.style.width = pct + '%';
        var bar = progressFill.parentElement;
        if (bar) {
          bar.setAttribute('aria-valuemin', '0');
          bar.setAttribute('aria-valuemax', '100');
          bar.setAttribute('aria-valuenow', String(pct));
        }
      }
    }

    /* ════════ Personalization ════════ */

    function applyPersonalization(block) {
      block.querySelectorAll('[data-personalize]').forEach(function (el) {
        var template = el.getAttribute('data-personalize');
        if (template) el.innerHTML = renderTemplate(template);
      });
    }

    function renderTemplate(str) {
      if (!str) return '';
      return str.replace(/\{(\w+)\}/g, function (match, key) {
        return (personalization[key] !== undefined && personalization[key] !== '')
          ? escapeHtml(String(personalization[key]))
          : (key === 'dog_name' ? 'your dog' : match);
      });
    }

    /* ════════ Routing engine — BUCKET SCORING ════════ */

    function completeAndRoute() {
      var resolved = resolveResultType();
      completed = true;
      tracker.track('result_assigned', {
        result_type: resolved.type,
        scores: resolved.scores,
        top_score: resolved.max
      });
      var url = buildResultUrl(resolved.url, resolved.type);
      log('Routing decision', resolved, '→', url);
      try { sessionStorage.removeItem(storageKey); } catch (e) {}
      setTimeout(function () { window.location.href = url; }, 60);
    }

    function resolveResultType() {
      var scores = computeScores();
      var bucketsAll = ['ft', 'gw', 'ab', 'te'];
      var priority = ['gw', 'ft', 'ab', 'te']; // health-urgency first
      var max = Math.max(scores.ft, scores.gw, scores.ab, scores.te);
      var tied = bucketsAll.filter(function (b) { return scores[b] === max; });

      var winner;
      if (tied.length === 1) {
        winner = tied[0];
      } else {
        var anchor = getTiebreakAnchorBucket();
        if (anchor && tied.indexOf(anchor) !== -1) {
          winner = anchor;
        } else {
          for (var i = 0; i < priority.length; i++) {
            if (tied.indexOf(priority[i]) !== -1) { winner = priority[i]; break; }
          }
        }
      }
      if (!winner || max <= 0) winner = fallbackBucket;

      return { type: winner, url: resultUrls[winner] || fallbackUrl, scores: scores, max: max };
    }

    function computeScores() {
      var s = { ft: 0, gw: 0, ab: 0, te: 0 };
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        if (a.type !== 'quiz-question') return;
        (a.selections || []).forEach(function (sel) {
          if (sel.bucket && s[sel.bucket] !== undefined) s[sel.bucket] += (parseInt(sel.weight, 10) || 0);
        });
        // Multi-select bonus rule (Q6: 3+ selections → te +2)
        var mb = a.multi_bonus;
        if (mb && mb.threshold > 0 && mb.bucket && s[mb.bucket] !== undefined) {
          if ((a.selections || []).length >= mb.threshold) s[mb.bucket] += mb.weight;
        }
      });
      return s;
    }

    function getTiebreakAnchorBucket() {
      var found = null;
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        if (a.type === 'quiz-question' && a.is_tiebreak_anchor && (a.selections || []).length) {
          found = a.selections[0].bucket; // Q4 is single-select
        }
      });
      return (found && found !== 'none') ? found : null;
    }

    function buildResultUrl(baseUrl, resultType) {
      var isRelative = baseUrl && baseUrl.indexOf('/') === 0 && baseUrl.indexOf('//') !== 0;
      var url;
      try { url = new URL(baseUrl, window.location.origin); }
      catch (e) { url = new URL(fallbackUrl || '/', window.location.origin); }

      url.searchParams.set('result_type', resultType);
      if (personalization.dog_name) url.searchParams.set('dog_name', personalization.dog_name);

      // q_<id> answer params for analytics / conditional rendering on the result page
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        if (!a.block_id) return;
        if (a.type === 'quiz-question') {
          var vals = (a.selections || []).map(function (s) { return s.value; }).join(',');
          if (vals) url.searchParams.set('q_' + a.block_id, vals);
        }
        // text-input value is the dog's name → already in dog_name; not duplicated here
      });

      // Forward inbound ad UTMs so attribution persists into the result page.
      // (The result-page CTA stamps utm_source=quiz&utm_medium=walk_diagnostic&
      //  utm_content=<result_type> onto the PDP link — handled in the result page.)
      if (forwardUtm) {
        var inc = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (key) {
          var v = inc.get(key);
          if (v) url.searchParams.set(key, v);
        });
      }

      return isRelative ? (url.pathname + url.search + url.hash) : url.toString();
    }

    /* ════════ Persistence ════════ */

    function persist() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          sessionId: sessionId, startedAt: startedAt, currentIdx: currentIdx,
          history: history, answers: answers, personalization: personalization
        }));
      } catch (e) { log('Persist failed', e); }
    }

    function rehydrate() {
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        var state = JSON.parse(raw);
        if (!state || !state.sessionId) return;
        if (Date.now() - state.startedAt > 7200000) { // 2h TTL
          sessionStorage.removeItem(storageKey); return;
        }
        sessionId = state.sessionId;
        startedAt = state.startedAt;
        currentIdx = typeof state.currentIdx === 'number' ? state.currentIdx : -1;
        history = state.history || [];
        answers = state.answers || {};
        personalization = Object.assign(personalization, state.personalization || {});
        startFired = currentIdx >= 0;
        log('Rehydrated', { currentIdx: currentIdx });
      } catch (e) { log('Rehydrate failed', e); }
    }

    // After binding: if we rehydrated mid-quiz, restore the active block,
    // re-apply answer visual state, and re-fire the block's behaviour.
    function resumeActiveBlock() {
      if (currentIdx < 0 || !blocks[currentIdx]) return;

      restoreAnswerVisuals();

      blocks.forEach(function (b) { b.classList.remove('quiz-dfw-block--active'); });
      var landing = root.querySelector('.quiz-dfw-block--landing');
      if (landing) landing.classList.remove('quiz-dfw-block--active');

      var cur = blocks[currentIdx];
      cur.classList.add('quiz-dfw-block--active');
      applyPersonalization(cur);
      // Re-fire behaviour for auto-advancing / routing blocks so a refresh
      // on an affirmation or the loading screen doesn't get stuck.
      cur.dispatchEvent(new CustomEvent('quiz:block-shown', { detail: { idx: currentIdx, resumed: true } }));
    }

    function restoreAnswerVisuals() {
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        var block = blocks[a.block_idx];
        if (!block) return;

        if (a.type === 'quiz-question') {
          (a.selections || []).forEach(function (sel) {
            var label = block.querySelector('.qdfw-arow[data-answer-value="' + cssEscape(sel.value) + '"]');
            if (label) {
              label.classList.add('qdfw-arow--selected');
              var input = label.querySelector('.qdfw-answer__input');
              if (input) input.checked = true;
            }
          });
          var nextBtn = block.querySelector('.qdfw-next');
          if (nextBtn) nextBtn.disabled = false;
        } else if (a.type === 'text-input') {
          var field = block.querySelector('[data-text-input]');
          if (field && a.value) {
            field.value = a.value;
            var tnext = block.querySelector('.qdfw-next');
            if (tnext) tnext.disabled = false;
          }
        }
      });
    }

    /* ════════ Utilities ════════ */

    function autoAdvanceDelay() {
      var v = parseInt(getCssVar('--auto-advance-delay'), 10);
      return isNaN(v) ? 360 : v;
    }

    function generateSessionId() {
      return 'dfw_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
    }

    function getCssVar(name) {
      var el = root.querySelector('.quiz-dfw') || root;
      try { return getComputedStyle(el).getPropertyValue(name).trim(); }
      catch (e) { return ''; }
    }

    function cssEscape(s) {
      if (window.CSS && CSS.escape) return CSS.escape(s);
      return String(s).replace(/["\\\]\[]/g, '\\$&');
    }

    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  }

  /* ════════ Tracker factory (Aeyla-style, slim) ════════ */

  function createTracker(root, sessionId, log) {
    var enabled = root.dataset.trackEnabled === 'true';
    var webhook = root.dataset.webhook || '';
    var metaPixel = root.dataset.metaPixel === 'true';
    var ga4 = root.dataset.ga4 === 'true';
    var variant = root.dataset.variant || '';
    var quizName = root.dataset.quizName || 'walk_diagnostic';
    var device = /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop';

    function track(eventType, data) {
      var payload = Object.assign({
        quiz: quizName, variant: variant, session_id: sessionId,
        device: device, ts: new Date().toISOString()
      }, data || {});
      log('track', eventType, payload);
      if (!enabled) return;

      // Webhook via image-pixel GET (CORS-safe, e.g. Google Apps Script)
      if (webhook) {
        try {
          var img = new Image();
          img.src = webhook + (webhook.indexOf('?') === -1 ? '?' : '&') +
            'event=' + encodeURIComponent('quiz_' + eventType) +
            '&data=' + encodeURIComponent(JSON.stringify(payload)) +
            '&t=' + Date.now();
        } catch (e) {}
      }
      // Meta Pixel custom event
      if (metaPixel && typeof window.fbq === 'function') {
        try { window.fbq('trackCustom', 'Quiz_' + eventType, payload); } catch (e) {}
      }
      // GA4 event
      if (ga4 && typeof window.gtag === 'function') {
        try { window.gtag('event', 'quiz_' + eventType, payload); } catch (e) {}
      }
    }

    return { track: track };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();